import React, { useMemo, useState } from 'react';
import './ConversionControlPage.css';
import { fmtMoney, getAllConversions, importConversions, toISO } from '../data/dataService';
import { generateConversionPreview } from '../data/conversionPreview';

const GEO_OPTIONS = [
  { code: 'PL', label: 'Польша' },
  { code: 'CZ', label: 'Чехия' },
  { code: 'SK', label: 'Словакия' },
];

function ConversionControlPage() {
  const conversions = useMemo(() => getAllConversions(), []);
  const [geos, setGeos] = useState(['PL', 'CZ', 'SK']);
  const [date, setDate] = useState(() => toISO(new Date()));
  const [targetRevenue, setTargetRevenue] = useState('1000');
  const [count, setCount] = useState('12');
  const [registrationsPerSale, setRegistrationsPerSale] = useState('10');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [importState, setImportState] = useState('idle');
  const [importMessage, setImportMessage] = useState('');

  const toggleGeo = (code) => setGeos((current) => (
    current.includes(code) ? current.filter((item) => item !== code) : [...current, code]
  ));

  const generate = (event) => {
    event.preventDefault();
    if (!geos.length) { setError('Выберите хотя бы одно гео.'); return; }
    if (!date || Number(count) < 1) { setError('Укажите дату и количество конверсий.'); return; }
    try {
      setPreview(generateConversionPreview({ conversions, geos, date, targetRevenue, count, registrationsPerSale }));
      setError('');
      setImportState('idle');
      setImportMessage('');
    } catch (generationError) {
      setError(generationError.message);
    }
  };

  const handleImport = async () => {
    if (!preview || importState === 'loading' || importState === 'done') return;
    setImportState('loading');
    setImportMessage('');
    try {
      const result = await importConversions(preview.rows);
      setImportState('done');
      setImportMessage(`Импортировано: ${result.inserted} конверсий. Будущие появятся в логе в своё время.`);
    } catch (importError) {
      setImportState('error');
      setImportMessage(importError.message);
    }
  };

  return (
    <div className="cc">
      <header className="cc__header">
        <div>
          <div className="cc__eyebrow">Режим предпросмотра</div>
          <h1>Генератор конверсий</h1>
          <p>Формирует реалистичный таймлайн на основе существующих офферов и выплат. В базу ничего не записывается.</p>
        </div>
        <a className="cc__back" href="/">Вернуться в кабинет</a>
      </header>

      <form className="cc__panel" onSubmit={generate}>
        <div className="cc__field cc__field--geo">
          <span className="cc__label">Гео</span>
          <div className="cc__checks">
            {GEO_OPTIONS.map((geo) => (
              <label key={geo.code} className={`cc__check ${geos.includes(geo.code) ? 'cc__check--active' : ''}`}>
                <input type="checkbox" checked={geos.includes(geo.code)} onChange={() => toggleGeo(geo.code)} />
                <span>{geo.label}</span><small>{geo.code}</small>
              </label>
            ))}
          </div>
        </div>
        <label className="cc__field"><span className="cc__label">Дата (Europe/Warsaw)</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="cc__field"><span className="cc__label">Целевая выручка, $</span><input type="number" min="0" step="0.01" value={targetRevenue} onChange={(e) => setTargetRevenue(e.target.value)} /></label>
        <label className="cc__field"><span className="cc__label">Количество регистраций</span><input type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} /></label>
        <label className="cc__field"><span className="cc__label">Регистраций на 1 депозит</span><input type="number" min="1" max="100" step="0.1" value={registrationsPerSale} onChange={(e) => setRegistrationsPerSale(e.target.value)} /></label>
        <button className="cc__generate" type="submit">Сгенерировать</button>
        {error && <div className="cc__error">{error}</div>}
      </form>

      {preview && (
        <section className="cc__result">
          <div className="cc__summary">
            <div><span>Всего строк</span><strong>{preview.summary.total}</strong></div>
            <div><span>Лидов</span><strong>{preview.summary.leads}</strong></div>
            <div><span>Продаж</span><strong>{preview.summary.sales}</strong></div>
            <div><span>Цель</span><strong>{fmtMoney(preview.summary.targetRevenue)}</strong></div>
            <div><span>Получилось</span><strong className="cc__money">{fmtMoney(preview.summary.actualRevenue)}</strong></div>
            <div><span>Отклонение</span><strong className={Math.abs(preview.summary.difference) < 1 ? 'cc__money' : ''}>{preview.summary.difference > 0 ? '+' : ''}{fmtMoney(preview.summary.difference)}</strong></div>
          </div>
          <div className="cc__notice">Продажа всегда имеет более ранний связанный лид; период продажи показывает точную разницу между ними.</div>
          <div className="cc__import-bar">
            <div><strong>Время хранится в UTC.</strong><span> Все строки импортируются сразу, но будущие скрыты в логе до наступления их времени.</span></div>
            <button type="button" onClick={handleImport} disabled={importState === 'loading' || importState === 'done'}>
              {importState === 'loading' ? 'Импорт…' : importState === 'done' ? 'Импортировано' : 'Импортировать в базу'}
            </button>
          </div>
          {importMessage && <div className={`cc__import-message cc__import-message--${importState}`}>{importMessage}</div>}
          <div className="cc__table-wrap">
            <table className="cc__table">
              <thead><tr><th>ID конверсии</th><th>Кампания</th><th>Оффер</th><th>Время конверсии</th><th>Период пр</th><th>Статус</th><th>Ориг. статус</th><th>Доход</th><th>Sub ID 1</th><th>Sub ID 2</th><th>Sub ID 3</th><th>Sub ID 4</th><th>Модель устройства</th><th>Город</th></tr></thead>
              <tbody>{preview.rows.map((row) => (
                <tr key={row.id} className={row.origStatus === 'sale' ? 'cc__sale-row' : ''}>
                  <td className="cc__id" title={`${row.id}${row.pairId ? ` · пара ${row.pairId}` : ''}`}>{row.id.slice(0, 18)}…</td>
                  <td title={row.campaign}>{row.campaign}</td><td className="cc__offer" title={row.offer}>{row.offer}</td>
                  <td className="cc__time">{row.conversionTime}</td><td>{row.period || ''}</td>
                  <td><span className={`cc__status cc__status--${row.origStatus}`}>{row.status}</span></td>
                  <td className="cc__orig-status">{row.origStatus}</td>
                  <td className={row.revenue ? 'cc__money' : 'cc__dash'}>{fmtMoney(row.revenue)}</td>
                  <td>{row.sub1}</td><td>{row.sub2}</td><td>{row.sub3}</td><td>{row.sub4}</td>
                  <td>{row.deviceModel}</td><td>{row.city}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default ConversionControlPage;
